/**
 * DV-INF-001: Demo Viewer Page (Infrastructure Aligned)
 * Admin-only diagnostic tool with unified auth, logging, and error handling
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Settings } from 'lucide-react';
import { useAdminAccess } from '@/components/demo-viewer/AdminGuard';
import { DemoErrorBoundaryWithLogging } from '@/components/demo-viewer/DemoErrorBoundary';
import { useDemoLogger } from '@/lib/demo-logger';
import { useDemoSelection, DemoPageSelector, TestBlock, LogsPanel, useTelemetry, WebSocketDemo, TemporalDemo, HeartbeatsDemo, AuthPanel, StageOutputsPreview, BillingCommitsViewer, StageTesterPage, type LogEntry } from '@/components/demo-viewer';
import { ExecutionSelector } from '@/components/demo-viewer/ExecutionSelector';
import { FeatureFlagsConsole } from '@/components/demo-viewer/FeatureFlagsConsole';

const DemoViewerPage: React.FC = () => {
  // DV-FIX-310: Always call ALL hooks unconditionally first to avoid hooks invariant issues
  const {
    adminLoading,
    isAdmin,
    canAccessAdminFeatures
  } = useAdminAccess();

  const navigate = useNavigate();
  const { currentPage, stepPage } = useDemoSelection();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [executionId, setExecutionId] = useState('fc97fdc0-24f5-4961-8c52-dae8e33b1187');
  const [renderToken] = useState(0);

  // DV-FIX-310: Throttled logging buffer to prevent feedback loops
  const logBufferRef = useRef<LogEntry[]>([]);
  const throttleTimeoutRef = useRef<NodeJS.Timeout>();

  const flushLogBuffer = useCallback(() => {
    if (logBufferRef.current.length > 0) {
      setLogs(prev => [...prev, ...logBufferRef.current].slice(-500)); // Keep last 500 logs
      logBufferRef.current = [];
    }
  }, []);

  // DV-FIX-310: Stable onLog callback that doesn't cause re-renders
  const handleDemoLog = useCallback((entry: LogEntry) => {
    // Buffer logs and flush with throttle to prevent feedback loops
    logBufferRef.current.push(entry);

    // Clear existing timeout and set new one
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
    }

    throttleTimeoutRef.current = setTimeout(flushLogBuffer, 16); // 16ms throttle (~60fps)
  }, [flushLogBuffer]);

  // DV-FIX-310: Unified logging with NO feedback to demoLogger - just buffer
  const demoLogger = useDemoLogger('ui', {
    component: 'DemoViewerPage',
    onLog: () => {
      // Do NOT call handleDemoLog here to avoid double logging
      // Demo logger already sends to its own log system
    }
  });

  // Initialize telemetry with unified logging - always initialize
  const { mark, measure, time } = useTelemetry({
    onLog: handleDemoLog
  });

  // Cleanup throttle timeout on unmount
  useEffect(() => {
    return () => {
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
        // Flush any remaining logs before unmount
        flushLogBuffer();
      }
    };
  }, [flushLogBuffer]);

  // DV-FIX-310: Define ALL handlers before any conditional returns

  // Back navigation with fallback to dashboard
  const handleBack = useCallback(() => {
    // Try to go back in history, but fallback to dashboard if no history
    // The navigate(-1) will work if there's actual browser history
    // If user came directly to demo viewer URL, it will go to dashboard
    try {
      // Check if we have referrer or if this is the first page in session
      if (document.referrer && document.referrer.includes(window.location.origin)) {
        navigate(-1);
      } else {
        // No referrer or external referrer - go to dashboard
        navigate('/dashboard');
      }
    } catch (error) {
      // Fallback to dashboard on any error
      navigate('/dashboard');
    }
  }, [navigate]);

  // Clear logs handler
  const handleClearLogs = useCallback(() => {
    setLogs([]);
    demoLogger.info('Logs cleared by user', 'clear', { action: 'clear' });
  }, [demoLogger]);

  // TestBlock timing wrapper
  const handleTestRun = useCallback(async (fn: () => void | Promise<void>) => {
    await time('test_block_run', async () => {
      await fn();
    }, { testBlock: 'Framework Components Test' });
  }, [time]);

  // Keyboard navigation for page switching
  useEffect(() => {
    console.log('🔄 DemoViewerPage: Keyboard navigation useEffect executed');
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return; // Don't interfere with form inputs
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        // Use replace for rapid navigation to avoid polluting history
        stepPage(-1, { replace: true });
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        stepPage(1, { replace: true });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - stepPage is stable enough for event handlers

  // DV-FIX-310: Lifecycle telemetry with StrictMode idempotence
  useEffect(() => {
    console.log('🔄 DemoViewerPage: Mount telemetry useEffect executed');

    // StrictMode-safe flag to prevent duplicate initialization
    let wasInitialized = false;

    if (!wasInitialized) {
      mark('demo_page_mount', { component: 'DemoViewerPage' });
      demoLogger.info('Demo Viewer initialized', 'init', { component: 'DemoViewerPage' });
      demoLogger.debug('Theme tokens loaded', 'init', { theme: 'agi' });
      demoLogger.warn('This is a sample warning message', 'init', { source: 'demo' });
      wasInitialized = true;
    }

    // Cleanup on unmount - only mark once per actual unmount
    return () => {
      if (wasInitialized) {
        mark('demo_page_unmount', { component: 'DemoViewerPage' });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - this should only run once on mount

  // DV-FIX-310: Page change telemetry with StrictMode idempotence and cleanup
  useEffect(() => {
    console.log('🔄 DemoViewerPage: Page change telemetry useEffect executed for page:', currentPage.id);
    const changeStartTime = performance.now();
    mark('demo_page_change_start');

    demoLogger.info(`Demo page changed to: ${currentPage.id}`, 'page_change', {
      pageId: currentPage.id,
      pageName: currentPage.name
    });

    // StrictMode-safe timer with cleanup
    const timeoutId = setTimeout(() => {
      const changeEndTime = performance.now();
      measure('demo_page_change', changeStartTime, changeEndTime, {
        pageId: currentPage.id,
        pageName: currentPage.name
      });
    }, 50); // Small delay to simulate page change processing

    // Cleanup timeout to prevent hanging timers in StrictMode
    return () => {
      clearTimeout(timeoutId);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage.id]); // Only depend on page ID, not the telemetry functions

  // DV-FIX-310: Auth readiness gate - single conditional return after all hooks
  if (adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center"
           style={{ backgroundColor: 'var(--bg-app)', color: 'var(--fg-primary)' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-current border-t-transparent rounded-full animate-spin mx-auto mb-4"
               style={{ borderColor: 'var(--accent)' }} />
          <p style={{ color: 'var(--fg-muted)' }}>Checking admin permissions...</p>
        </div>
      </div>
    );
  }

  // DV-FIX-310: If not admin, return access denied (no AdminGuard wrapper needed)
  if (!isAdmin || !canAccessAdminFeatures) {
    return (
      <div className="min-h-screen flex items-center justify-center"
           style={{ backgroundColor: 'var(--bg-app)' }}>
        <div className="max-w-md mx-auto text-center">
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
            <h1 className="text-xl font-semibold text-red-400 mb-2">Admin Access Required</h1>
            <p className="text-white/60 mb-4">
              Demo Viewer is only available to admin users.
            </p>
            <button
              onClick={handleBack}
              className="inline-flex items-center px-4 py-2 bg-cyan-500/20 border border-cyan-500/30
                       rounded-lg text-cyan-400 hover:bg-cyan-500/30 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // DV-FIX-310: Render admin content with error boundary (no AdminGuard wrapper needed)
  return (
    <DemoErrorBoundaryWithLogging
      source="DemoViewerPage"
      onLog={handleDemoLog}
      resetKeys={[currentPage.id, executionId, renderToken]}
    >
        <div
          className="min-h-screen"
          style={{
            backgroundColor: 'var(--bg-app)',
            color: 'var(--fg-primary)',
          }}
        >
      {/* Top Bar */}
      <div
        className="border-b backdrop-blur-xl"
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderColor: 'var(--border-subtle)',
        }}
      >
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              data-testid="dv-back"
              className="inline-flex items-center px-3 py-2 rounded-lg border transition-all focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                borderColor: 'var(--border-default)',
                color: 'var(--fg-muted)',
                // Hover states
                ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
                ['--hover-color' as any]: 'var(--fg-primary)',
                ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                e.currentTarget.style.color = 'var(--hover-color)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-elevated)';
                e.currentTarget.style.color = 'var(--fg-muted)';
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = 'var(--focus-ring)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>

            <DemoPageSelector />
          </div>

          <div
            className="text-sm font-mono"
            style={{
              color: 'var(--fg-muted)',
              fontFamily: 'var(--font-family-mono)',
            }}
          >
            Use ← → arrows to navigate
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1
              className="text-2xl font-light mb-2"
              style={{ color: 'var(--accent)' }}
            >
              Demo Viewer: {currentPage.name}
            </h1>
            <p style={{ color: 'var(--fg-muted)' }}>
              {currentPage.description}
            </p>
          </div>

          {/* Demo Content */}
          <div className="space-y-6 mb-6">
            {currentPage.id === 'websockets' ? (
              /* DV-WS-001: WebSocket Demo */
              <WebSocketDemo onLog={handleDemoLog} />
            ) : currentPage.id === 'temporal' ? (
              /* DV-TEMP-001: Temporal Demo */
              <TemporalDemo
                executionId={executionId}
                onExecutionChange={setExecutionId}
                onLog={handleDemoLog}
              />
            ) : currentPage.id === 'heartbeats' ? (
              /* DV-TEMP-002: Heartbeats Demo */
              <HeartbeatsDemo
                executionId={executionId}
                onExecutionChange={setExecutionId}
                onLog={handleDemoLog}
              />
            ) : currentPage.id === 'stage-tester' ? (
              /* DV-STAGE-001: Stage Tester */
              <StageTesterPage
                onLog={handleDemoLog}
                data-testid="dv-stage-tester"
              />
            ) : currentPage.id === 'feature-flags' ? (
              /* DV-FF-001: Feature Flags Console */
              <FeatureFlagsConsole
                onLog={handleDemoLog}
                data-testid="dv-feature-flags-console"
              />
            ) : currentPage.id === 'auth' ? (
              /* DV-AUTH-001: Auth Panel */
              <AuthPanel
                onLog={handleDemoLog}
                data-testid="dv-auth-panel"
              />
            ) : currentPage.id === 'stage-outputs' ? (
              /* DV-STOR-001: Stage Outputs Preview */
              <div className="space-y-6">
                <ExecutionSelector
                  selectedExecutionId={executionId}
                  onExecutionChange={setExecutionId}
                  className="max-w-lg"
                />
                <StageOutputsPreview
                  executionId={executionId}
                  onLog={handleDemoLog}
                  data-testid="dv-stage-outputs-preview"
                />
              </div>
            ) : currentPage.id === 'billing' ? (
              /* DV-BILL-001: Billing Commits Viewer */
              <BillingCommitsViewer
                executionId={executionId}
                onExecutionChange={setExecutionId}
                onLog={handleDemoLog}
                data-testid="dv-billing-commits-viewer"
              />
            ) : (
              /* Framework Test Blocks for other pages */
              <>
                <TestBlock
                  title="Framework Components Test"
                  description="Testing the DV-002 framework components"
                  status="success"
                  onRun={handleTestRun}
                  actions={
                    <div className="flex items-center gap-2">
                      <button
                        className="flex items-center gap-1 px-2 py-1 rounded text-xs border"
                        style={{
                          backgroundColor: 'var(--bg-surface)',
                          borderColor: 'var(--border-default)',
                          color: 'var(--fg-primary)',
                        }}
                        onClick={() => demoLogger.userAction('Manual test triggered', { action: 'test' })}
                      >
                        <Play className="w-3 h-3" />
                        Test
                      </button>
                      <button
                        className="flex items-center gap-1 px-2 py-1 rounded text-xs border"
                        style={{
                          backgroundColor: 'var(--bg-surface)',
                          borderColor: 'var(--border-default)',
                          color: 'var(--fg-primary)',
                        }}
                      >
                        <Settings className="w-3 h-3" />
                        Config
                      </button>
                    </div>
                  }
                >
                  <div className="space-y-4">
                    <div
                      className="p-4 rounded border"
                      style={{
                        backgroundColor: 'var(--bg-elevated)',
                        borderColor: 'var(--border-subtle)',
                      }}
                    >
                      <p
                        className="text-sm mb-2"
                        style={{ color: 'var(--fg-primary)' }}
                      >
                        Current Demo Page: <span style={{ color: 'var(--accent)' }}>{currentPage.id}</span>
                      </p>
                      <p
                        className="text-xs"
                        style={{ color: 'var(--fg-muted)' }}
                      >
                        URL-driven selection is working. Try changing pages with the dropdown or arrow keys.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        className="p-3 rounded border text-center"
                        style={{
                          backgroundColor: 'var(--bg-elevated)',
                          borderColor: 'var(--border-subtle)',
                        }}
                      >
                        <div style={{ color: 'var(--accent)' }} className="text-sm font-mono">
                          {logs.length}
                        </div>
                        <div style={{ color: 'var(--fg-muted)' }} className="text-xs">
                          Total Logs
                        </div>
                      </div>
                      <div
                        className="p-3 rounded border text-center"
                        style={{
                          backgroundColor: 'var(--bg-elevated)',
                          borderColor: 'var(--border-subtle)',
                        }}
                      >
                        <div style={{ color: 'var(--accent)' }} className="text-sm font-mono">
                          {logs.filter(log => log.level === 'error').length}
                        </div>
                        <div style={{ color: 'var(--fg-muted)' }} className="text-xs">
                          Error Logs
                        </div>
                      </div>
                    </div>
                  </div>
                </TestBlock>

                <TestBlock
                  title={`${currentPage.name} Demo Tests`}
                  description="Placeholder for page-specific demo functionality"
                  status="info"
                  defaultOpen={false}
                >
                  <div
                    className="p-4 rounded text-center"
                    style={{
                      backgroundColor: 'var(--bg-elevated)',
                      borderColor: 'var(--border-subtle)',
                    }}
                  >
                    <p style={{ color: 'var(--fg-muted)' }} className="text-sm">
                      Demo functionality for {currentPage.name} will be implemented in subsequent tickets
                      (DV-WS-001, DV-TEMP-001, DV-AUTH-001).
                    </p>
                  </div>
                </TestBlock>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Logs Panel - Show for non-demo-specific pages and stage-tester */}
      {(currentPage.id === 'stage-tester' ||
        (currentPage.id !== 'websockets' &&
         currentPage.id !== 'temporal' &&
         currentPage.id !== 'heartbeats' &&
         currentPage.id !== 'feature-flags' &&
         currentPage.id !== 'auth' &&
         currentPage.id !== 'stage-outputs' &&
         currentPage.id !== 'billing')) && (
        <div
          className="border-t"
          style={{
            backgroundColor: 'var(--bg-surface)',
            borderColor: 'var(--border-subtle)',
          }}
        >
          <div className="p-4">
            <LogsPanel
              entries={logs}
              onClear={handleClearLogs}
              minHeight={200}
              maxHeight={400}
              data-testid="dv-logs-panel"
            />
          </div>
        </div>
      )}
    </div>
    </DemoErrorBoundaryWithLogging>
  );
};

export default DemoViewerPage;